import { useRef, useState } from "react";
import { toast } from "sonner";
import { CarFront, Upload, X } from "lucide-react";
import SectionCard, { inputCls } from "./SectionCard";
import { api, photoUrl, uploadFiles, formatApiError } from "../../api";
import { PAINT_PARTS, PAINT_META, PAINT_STATUS_OPTIONS, editTimeLeft } from "../../lib/labels";

function PaintPartView({ part, cond, det }) {
  const meta = PAINT_META[cond] || PAINT_META.belirtilmedi;
  return (
    <div data-testid={`kaporta-view-${part.key.replace(/_/g, "-")}`}
      className={`rounded-xl border px-4 py-3 ${meta.cls}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-200">{part.label}</span>
        <span className="text-xs font-bold whitespace-nowrap">{meta.label}</span>
      </div>
      {(det.note || det.photo) && (
        <div className="mt-2 flex items-center gap-2">
          {det.photo && <img src={photoUrl(det.photo)} alt="" className="w-12 h-9 object-cover rounded border border-white/10" />}
          {det.note && <p className="text-xs text-slate-400 line-clamp-2">{det.note}</p>}
        </div>
      )}
    </div>
  );
}

function PaintPartEditor({ part, status, note, photo, uploading, registerRef, onUploadClick, onStatus, onNote, onPickFile, onRemovePhoto }) {
  const tid = part.key.replace(/_/g, "-");
  return (
    <div data-testid={`kaporta-edit-${tid}`} className="rounded-xl border border-white/10 bg-slate-950/50 p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-sm font-semibold text-slate-100 sm:w-40 shrink-0">{part.label}</span>
        <select data-testid={`kaporta-status-${tid}`} value={status} onChange={(e) => onStatus(e.target.value)} className={inputCls}>
          {PAINT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <input data-testid={`kaporta-note-${tid}`} value={note} onChange={(e) => onNote(e.target.value)}
        className={inputCls} placeholder="Açıklama (opsiyonel)" />
      <div className="flex items-center gap-3">
        <input type="file" accept="image/*" className="hidden" ref={registerRef}
          onChange={(e) => { if (e.target.files[0]) onPickFile(e.target.files[0]); e.target.value = ""; }} />
        <button type="button" data-testid={`kaporta-photo-${tid}`} onClick={onUploadClick} disabled={uploading}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-dashed border-white/20 text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Yükleniyor..." : "Fotoğraf Ekle"}
        </button>
        {photo && (
          <div className="relative">
            <img src={photoUrl(photo)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />
            <button type="button" onClick={onRemovePhoto}
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaintSection({ vehicle, onSaved }) {
  const [editing, setEditing] = useState(false);
  const paintLocked = (vehicle.locked_sections || []).includes("paint");
  const paintStamp = (vehicle.section_locks || {}).paint;
  const paintTimeLeft = !paintLocked && paintStamp ? editTimeLeft(paintStamp) : null;
  const [saving, setSaving] = useState(false);
  const [paint, setPaint] = useState(null);
  const [details, setDetails] = useState(null);
  const [uploadingPart, setUploadingPart] = useState(null);
  const fileRefs = useRef({});

  const startEdit = () => {
    const p = {};
    const d = {};
    PAINT_PARTS.forEach((part) => {
      p[part.key] = (vehicle.paint && vehicle.paint[part.key]) || "belirtilmedi";
      const det = (vehicle.paint_details && vehicle.paint_details[part.key]) || {};
      d[part.key] = { note: det.note || "", photo: det.photo || "" };
    });
    setPaint(p);
    setDetails(d);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/my-vehicles/${vehicle.id}/section/paint`, { value: paint });
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/section/paint_details`, { value: details });
      onSaved(data);
      setEditing(false);
      toast.success("Kaporta / boya durumu kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pickFile = async (key, file) => {
    setUploadingPart(key);
    try {
      const [path] = await uploadFiles([file]);
      setDetails((d) => ({ ...d, [key]: { ...d[key], photo: path } }));
    } catch (err) { toast.error(formatApiError(err)); }
    setUploadingPart(null);
  };

  return (
    <SectionCard testid="section-kaporta" title="Kaporta / Boya Durumu" Icon={CarFront}
      desc="Her parçayı ayrı ayrı değerlendirin; açıklama ve fotoğraf ekleyebilirsiniz."
      editing={editing} onEdit={startEdit} onCancel={() => setEditing(false)} onSave={save} saving={saving} hideActions={paintLocked}>
      {paintLocked && (
        <p data-testid="section-kaporta-locked" className="mb-3 text-[11px] font-semibold text-amber-300/90 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          Bu bölüm 24 saatlik düzenleme süresi dolduğu için kilitlendi; araç geçmişi değiştirilemez.
        </p>
      )}
      {paintTimeLeft && (
        <p data-testid="section-kaporta-countdown" className="mb-3 text-[11px] font-semibold text-cyan-300/90 rounded-lg border border-cyan-400/30 bg-cyan-400/5 px-3 py-2">
          Düzenleme süresi: {paintTimeLeft} kaldı — süre dolunca bu bölüm kalıcı olarak kilitlenir.
        </p>
      )}
      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PAINT_PARTS.map((p) => (
            <PaintPartView key={p.key} part={p}
              cond={(vehicle.paint && vehicle.paint[p.key]) || "belirtilmedi"}
              det={(vehicle.paint_details && vehicle.paint_details[p.key]) || {}} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {PAINT_PARTS.map((p) => (
            <PaintPartEditor key={p.key} part={p}
              status={paint[p.key]} note={details[p.key].note} photo={details[p.key].photo}
              uploading={uploadingPart === p.key}
              registerRef={(el) => (fileRefs.current[p.key] = el)}
              onUploadClick={() => fileRefs.current[p.key]?.click()}
              onStatus={(v) => setPaint({ ...paint, [p.key]: v })}
              onNote={(v) => setDetails({ ...details, [p.key]: { ...details[p.key], note: v } })}
              onPickFile={(f) => pickFile(p.key, f)}
              onRemovePhoto={() => setDetails((d) => ({ ...d, [p.key]: { ...d[p.key], photo: "" } }))} />
          ))}
        </div>
      )}
    </SectionCard>
  );
}
